import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    ActionIcon,
    Alert,
    Button,
    Card,
    Group,
    Loader,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconTrash, IconUserPlus } from '@tabler/icons-react';
import {
    createAdminUser,
    deleteAdminUser,
    listAdminUsers,
    type AdminUser,
} from '../api/adminUsers';

const ADMIN_USERS_QUERY_KEY = ['admin-users'];

/**
 * Admin management view (`/admins`). Lists the vendor-portal admin allowlist and
 * lets an admin add (promote) or remove (demote) admins. The route is only
 * reachable when the current caller is an admin (`useIsAdmin`), and the VGS
 * mutations are admin-gated in prod, so this is defense-in-depth on the UI side.
 */
export const AdminUsersView = (): React.JSX.Element => {
    const queryClient = useQueryClient();
    const [email, setEmail] = useState('');

    const query = useQuery({
        queryKey: ADMIN_USERS_QUERY_KEY,
        queryFn: () => listAdminUsers({ limit: 250 }),
    });

    const createMutation = useMutation({
        mutationFn: (value: string) => createAdminUser(value),
        onSuccess: (created) => {
            void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
            setEmail('');
            notifications.show({
                color: 'green',
                title: 'Admin added',
                message: `${created.email} can now manage the catalog and admins.`,
            });
        },
        onError: (err) => {
            notifications.show({
                color: 'red',
                title: 'Could not add admin',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => deleteAdminUser(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
            notifications.show({
                color: 'green',
                title: 'Admin removed',
                message: 'The admin was demoted.',
            });
        },
        onError: (err) => {
            notifications.show({
                color: 'red',
                title: 'Could not remove admin',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const admins: AdminUser[] = query.data?.data ?? [];
    const trimmedEmail = email.trim();

    const handleAdd = (): void => {
        if (trimmedEmail === '') {
            return;
        }
        createMutation.mutate(trimmedEmail);
    };

    return (
        <Stack gap="xl" flex={1} mih={0} data-testid="AdminUsersView">
            <div>
                <Title order={2}>Admins</Title>
                <Text size="sm" c="dimmed">
                    Manage who can promote, delete, and create admins, and who can manually edit the
                    company catalog. Enforced in production only.
                </Text>
            </div>

            <ScrollArea flex={1} mih={0}>
                <Stack gap="xl">
                    <Card withBorder padding="lg" radius="md" data-testid="AddAdminCard">
                        <Stack gap="md">
                            <Title order={4}>Add an admin</Title>
                            <Group align="flex-end" gap="sm">
                                <TextInput
                                    label="Email"
                                    placeholder="person@drata.com"
                                    type="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.currentTarget.value);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleAdd();
                                        }
                                    }}
                                    style={{ flex: 1, minWidth: 320 }}
                                    data-testid="AddAdminEmailInput"
                                />
                                <Button
                                    leftSection={<IconUserPlus size={16} />}
                                    disabled={trimmedEmail === ''}
                                    loading={createMutation.isPending}
                                    onClick={handleAdd}
                                    data-testid="AddAdminSubmit"
                                >
                                    Add admin
                                </Button>
                            </Group>
                        </Stack>
                    </Card>

                    <Card withBorder padding="lg" radius="md" data-testid="AdminListCard">
                        <Stack gap="md">
                            <Group justify="space-between">
                                <Title order={4}>Current admins</Title>
                                <Text size="sm" c="dimmed">
                                    {admins.length} admin{admins.length === 1 ? '' : 's'}
                                </Text>
                            </Group>

                            {query.isLoading ? <Loader size="sm" /> : null}
                            {query.error ? (
                                <Alert
                                    icon={<IconAlertTriangle size={16} />}
                                    color="red"
                                    title="Could not load admins"
                                >
                                    {query.error instanceof Error
                                        ? query.error.message
                                        : 'Unknown error'}
                                </Alert>
                            ) : null}

                            {!query.isLoading && !query.error ? (
                                <Table.ScrollContainer minWidth={560}>
                                    <Table striped highlightOnHover withTableBorder>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Email</Table.Th>
                                                <Table.Th>Added by</Table.Th>
                                                <Table.Th>Added</Table.Th>
                                                <Table.Th w={80} />
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {admins.length === 0 ? (
                                                <Table.Tr>
                                                    <Table.Td colSpan={4}>
                                                        <Text size="sm" c="dimmed">
                                                            No admins yet.
                                                        </Text>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ) : (
                                                admins.map((admin) => (
                                                    <Table.Tr key={admin.id}>
                                                        <Table.Td>{admin.email}</Table.Td>
                                                        <Table.Td>
                                                            {admin.created_by ?? '—'}
                                                        </Table.Td>
                                                        <Table.Td>
                                                            {new Date(
                                                                admin.created_at,
                                                            ).toLocaleDateString()}
                                                        </Table.Td>
                                                        <Table.Td>
                                                            <ActionIcon
                                                                variant="subtle"
                                                                color="red"
                                                                aria-label={`Remove ${admin.email}`}
                                                                loading={
                                                                    deleteMutation.isPending &&
                                                                    deleteMutation.variables ===
                                                                        admin.id
                                                                }
                                                                onClick={() => {
                                                                    deleteMutation.mutate(admin.id);
                                                                }}
                                                                data-testid={`RemoveAdmin-${String(admin.id)}`}
                                                            >
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Table.Td>
                                                    </Table.Tr>
                                                ))
                                            )}
                                        </Table.Tbody>
                                    </Table>
                                </Table.ScrollContainer>
                            ) : null}
                        </Stack>
                    </Card>
                </Stack>
            </ScrollArea>
        </Stack>
    );
};
